import React, { FC, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import FilesImagesUploadAreaAndTable from 'components/shared/files-images-upload-area-and-table/files-images-upload-area-and-table.component';
import DeliveryDatePicker from 'components/shared/delivery-date-picker/delivery-date-picker.component';
import { Order } from 'models/order';
import { useMutation } from '@apollo/client';
import { commentsTemplate, resendLabOrderByDentistTemplate } from 'apollo/templates/order';
import axios from 'axios';
import { useUploadFailureHandler } from 'hooks/useUploadFailureHandler';
import { startUploadAttempt } from 'utils/upload-failure';
import Cookies from 'js-cookie';
import { CONSTANTS } from 'config/constants';
import { File as DenthubFile, FileSource } from 'models/file';
import { LoadingButton } from '@mui/lab';
import { useSeedOnOpen } from 'hooks/useSeedOnOpen';

type ResendLabOrderByDentistDialogProps = {
    open: boolean;
    order: Order;
    onClose: () => void;
    onUpdate?: (order: Order) => void;
};

const ResendLabOrderByDentistDialog: FC<ResendLabOrderByDentistDialogProps> = ({ open, order, onClose, onUpdate }) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const handleUploadFailure = useUploadFailureHandler();

    const [comment, setComment] = useState('');
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
    const [deliveryDate, setDeliveryDate] = useState<Date | null>(
        order.deliveryDetails?.date ? new Date(order.deliveryDetails.date) : null,
    );
    const [deliveryHour, setDeliveryHour] = useState<number | undefined>(order.deliveryDetails?.time?.hour);
    const [deliveryMinute, setDeliveryMinute] = useState<number | undefined>(order.deliveryDetails?.time?.minute);
    const [resendLabOrderByDentist, { loading: isSubmitting }] = useMutation(resendLabOrderByDentistTemplate);

    // The useState seeds above only ran at mount; seed from the order as it is when opened.
    useSeedOnOpen(open, () => {
        setDeliveryDate(order.deliveryDetails?.date ? new Date(order.deliveryDetails.date) : null);
        setDeliveryHour(order.deliveryDetails?.time?.hour);
        setDeliveryMinute(order.deliveryDetails?.time?.minute);
    });

    const isDeliveryDateInPast = deliveryDate !== null && deliveryDate < new Date();

    const uploadFiles = async (files: File[]): Promise<DenthubFile[]> => {
        const formData = new FormData();
        for (const file of files) {
            formData.append('files', file, file.name);
        }
        // The order already exists here, so upload straight into its own folder rather
        // than the staging prefix. Resending is not order creation, so nothing on this
        // path used to repoint the File records afterwards (DD-3252 Phase 0.3); the
        // backend guard in promoteTempOrderFiles now covers stale clients too.
        const uploadDirectory =
            order.customer?._id && order._id
                ? `organizations/${order.customer._id}/orders/${order._id}`
                : 'temp/uploads';
        const response = await axios.post(
            `${CONSTANTS.DOC_API_URI}/upload/files?directory=${uploadDirectory}&source=${FileSource.ORDER_ATTACHMENTS}`,
            formData,
            { headers: { Authorization: Cookies.get(CONSTANTS.TOKEN_KEY) } },
        );
        return response.data ?? [];
    };

    const handleDeliveryChange = (date: Date | null, hour: number | undefined, minute: number | undefined) => {
        setDeliveryDate(date);
        setDeliveryHour(hour);
        setDeliveryMinute(minute);
    };

    const handleReset = (updatedOrder?: Order) => {
        setComment('');
        setDroppedFiles([]);
        setDeliveryDate(
            updatedOrder?.deliveryDetails?.date
                ? new Date(updatedOrder.deliveryDetails.date)
                : order.deliveryDetails?.date
                  ? new Date(order.deliveryDetails.date)
                  : null,
        );
        setDeliveryHour(updatedOrder?.deliveryDetails?.time?.hour ?? order.deliveryDetails?.time?.hour);
        setDeliveryMinute(updatedOrder?.deliveryDetails?.time?.minute ?? order.deliveryDetails?.time?.minute);
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleSubmit = async () => {
        const attempt = startUploadAttempt(droppedFiles);
        try {
            const uploadedFiles = droppedFiles.length > 0 ? await uploadFiles(droppedFiles) : [];
            const { data } = await resendLabOrderByDentist({
                variables: {
                    orderId: order._id,
                    order: {
                        deliveryDateTime: {
                            date: deliveryDate ? deliveryDate.getTime() : order.deliveryDetails.date,
                            time: { hour: deliveryHour, minute: deliveryMinute },
                        },
                        files: uploadedFiles.map((uploadedFile) => ({ file: uploadedFile._id })),
                        additionalInfo: comment,
                    },
                },
                refetchQueries: [{ query: commentsTemplate, variables: { orderId: order._id } }],
            });
            onUpdate?.(data.resendLabOrderByDentist);
            handleReset(data.resendLabOrderByDentist);
            onClose();
        } catch (error) {
            // A failed file upload gets the retry dialog; GraphQL errors still go to the boundary.
            handleUploadFailure(error, { upload: 'resend-order-files', attempt, retry: () => handleSubmit() });
        }
    };

    const handleChange = async (currentFiles: File[]) => {
        if (!currentFiles || currentFiles.length === 0) {
            return;
        }
        setDroppedFiles([...droppedFiles, ...currentFiles]);
    };

    const removeFile = (index: number) => {
        const currentFiles = [...droppedFiles];
        currentFiles.splice(index, 1);
        setDroppedFiles(currentFiles);
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={handleClose}
            title={translate('orders:LAB_ORDER_RESEND_ORDER')}
            actions={
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        data-test="resend-lab-order-by-dentist-dialog.cancel-btn"
                    >
                        {translate('common:CANCEL')}
                    </Button>

                    <LoadingButton
                        id="LAB_ORDER_RESEND_ORDER_SUBMIT"
                        loading={isSubmitting}
                        type="button"
                        fullWidth
                        variant="contained"
                        disabled={isDeliveryDateInPast || !comment}
                        data-test="resend-lab-order-by-dentist-dialog.loading-submit-btn"
                        onClick={handleSubmit}
                    >
                        {translate('orders:LAB_ORDER_RESEND_ORDER_SUBMIT')}
                    </LoadingButton>
                </Box>
            }
        >
            <Box display="flex" flexDirection="column" gap={2}>
                <Alert severity="info">{translate('orders:LAB_ORDER_RESEND_ORDER_INFO')}</Alert>

                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('orders:COMMENT')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        placeholder={translate('orders:LAB_ORDER_RESEND_ORDER_COMMENT_PLACEHOLDER')}
                        inputProps={{ 'data-test': 'resend-lab-order-by-dentist-dialog.comment-input' }}
                    />
                </Box>

                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('orders:FILES_AND_IMAGES')}
                    </Typography>

                    <FilesImagesUploadAreaAndTable
                        files={droppedFiles as any}
                        onDeleteFile={() => console.log('TODO')}
                        onFileChange={handleChange}
                        showAsSimpleList={true}
                        handleRemoveFile={removeFile}
                    />
                </Box>

                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('orders:DELIVERY_TIME')}
                    </Typography>
                    <DeliveryDatePicker
                        date={deliveryDate}
                        hour={deliveryHour}
                        minute={deliveryMinute}
                        onChange={handleDeliveryChange}
                        dataTest="resend-lab-order-by-dentist-dialog.delivery-date"
                    />
                </Box>
            </Box>
        </DHDialog>
    );
};

export default ResendLabOrderByDentistDialog;
